"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { formatPrice, type Currency } from "./format";

interface CurrencyContextValue {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  rates: Partial<Record<Currency, number>>;
  ratesFetchedAt: string | null;
  /** Currencies whose rate is an admin-set manual override, not the feed. */
  manualRates: Currency[];
  format: (usd: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({
  children,
  initialCurrency,
  rates,
  ratesFetchedAt,
  manualRates = [],
}: {
  children: ReactNode;
  initialCurrency: Currency;
  rates: Partial<Record<Currency, number>>;
  ratesFetchedAt: string | null;
  manualRates?: Currency[];
}) {
  const [currency, setCurrencyState] = useState<Currency>(initialCurrency);

  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    document.cookie = `pk-currency=${c};path=/;max-age=31536000;samesite=lax`;
  }, []);

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      rates,
      ratesFetchedAt,
      manualRates,
      format: (usd: number) => formatPrice(usd, currency, rates),
    }),
    [currency, setCurrency, rates, ratesFetchedAt, manualRates]
  );

  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency outside CurrencyProvider");
  return ctx;
}

/** Inline price: converts USD to the selected currency with tabular numerals.
 *  `compact` abbreviates 7-figure converted values for tight layouts. */
export function Price({
  usd,
  className,
  compact = false,
}: {
  usd: number | null;
  className?: string;
  compact?: boolean;
}) {
  const { currency, rates } = useCurrency();
  if (usd == null) return <span className={className}>—</span>;
  return (
    <span className={`tnum ${className ?? ""}`}>
      {formatPrice(usd, currency, rates, { compact })}
    </span>
  );
}
